import Cookies from "js-cookie";
import { ENUM_AUTH_TOKENS } from "./enums/auth.enums";
import { ENV_VARS } from "@/constants/env";
import { API_HEALTH, AUTH_LOGOUT, AUTH_REFRESH } from "@/constants/apiEndpoints";

const isHttps = () => window.location.protocol === "https:";

const setCookie = (name: string, value: string, days = 7) => {
  Cookies.set(name, value, {
    expires: days,
    path: "/",
    sameSite: "lax",
    secure: isHttps(),
  });
};

export const getCookie = (name: string): string | null => {
  return Cookies.get(name) ?? null;
};

export const setAuthTokensToCookie = (
  accessToken: string,
  refreshToken?: string | null,
  deviceId?: string,
  rememberMe?: boolean,
  email?: string,
) => {
  const tokenData = JSON.stringify({
    [ENUM_AUTH_TOKENS.ACCESS_TOKEN]: accessToken,
    [ENUM_AUTH_TOKENS.REFRESH_TOKEN]: refreshToken,
    [ENUM_AUTH_TOKENS.DEVICE_ID]: deviceId,
    [ENUM_AUTH_TOKENS.REMEMBERME]: rememberMe,
  });
  const days = rememberMe ? 30 : 1; // 30 days if remembered, 1 day if not
  setCookie(ENUM_AUTH_TOKENS.STORAGE_KEY, tokenData, days);

  // Separate cookie, untouched on logout
  if (rememberMe && email) setCookie(ENUM_AUTH_TOKENS.REMEMBERED_EMAIL, email, 30);
  else Cookies.remove(ENUM_AUTH_TOKENS.REMEMBERED_EMAIL);
};

export const getAuthTokensFromLocalStorage = (): {
  [ENUM_AUTH_TOKENS.ACCESS_TOKEN]: string;
  [ENUM_AUTH_TOKENS.REFRESH_TOKEN]: string;
  [ENUM_AUTH_TOKENS.DEVICE_ID]: string;
  [ENUM_AUTH_TOKENS.REMEMBERME]: boolean;
} | null => {
  const authTokens = getCookie(ENUM_AUTH_TOKENS.STORAGE_KEY);
  if (!authTokens) return null;
  try {
    return JSON.parse(authTokens);
  } catch (e) {
    console.error("Failed to parse auth tokens from cookies", e);
    return null;
  }
};

export const clearAuthTokens = () => {
  Cookies.remove(ENUM_AUTH_TOKENS.STORAGE_KEY, {
    path: "/",
  });
  Cookies.remove(ENUM_AUTH_TOKENS.IMPERSONATION_META, {
    path: "/",
  });
};

export interface ImpersonationMeta {
  impersonationId: string;
  adminEmail: string;
  adminName: string;
  adminProfileImage?: string | null;
  startedAt: string;
  expiresAt: string;
}

export const setImpersonationMeta = (meta: ImpersonationMeta) => {
  setCookie(ENUM_AUTH_TOKENS.IMPERSONATION_META, JSON.stringify(meta), 1);
};

export const getImpersonationMeta = (): ImpersonationMeta | null => {
  const raw = getCookie(ENUM_AUTH_TOKENS.IMPERSONATION_META);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationMeta;
  } catch {
    return null;
  }
};

export const clearImpersonationMeta = () => {
  Cookies.remove(ENUM_AUTH_TOKENS.IMPERSONATION_META, { path: "/" });
};

interface JwtPayload {
  exp: number;
  sub: number;
}

interface RefreshApiResponse {
  data: {
    accessToken: string;
    refreshToken: string;
  };
}

export const decodeTokenExpiry = (token: string): number | null => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as JwtPayload;
    return payload.exp ?? null;
  } catch {
    return null;
  }
};

// Milliseconds until the stored access token expires.
// Negative = already expired. null = no token / unparseable.
// Reads the cookie fresh each call — cookie is shared across tabs, so this is
// how a tab discovers that ANOTHER tab already refreshed.
export const getAccessTokenMsRemaining = (): number | null => {
  const stored = getAuthTokensFromLocalStorage();
  const token = stored?.[ENUM_AUTH_TOKENS.ACCESS_TOKEN];
  if (!token) return null;
  const exp = decodeTokenExpiry(token);
  if (!exp) return null;
  return exp * 1000 - Date.now();
};

// Best-effort: notify BE to set disconnectedAt + clean Redis.
// Fires and forgets — local state gets cleared regardless of result.
export const callServerLogout = async (): Promise<void> => {
  const stored = getAuthTokensFromLocalStorage();
  const accessToken = stored?.[ENUM_AUTH_TOKENS.ACCESS_TOKEN];
  if (!accessToken) return;
  try {
    await fetch(`${ENV_VARS.API_INSTANCE}${AUTH_LOGOUT}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch {
    // ignore — local cleanup still proceeds
  }
};

// Why a refresh failed decides what the app does next:
//   "rejected"   — the BE answered and refused the refresh token → session is
//                  genuinely dead → log out.
//   "network"    — the BE never answered (down, restarting, offline) → the
//                  session is probably fine → show the connection-lost screen
//                  and KEEP the tokens.
//   "no-token"   — nothing stored to refresh with → log out.
export type RefreshFailureReason = "rejected" | "network" | "no-token";

export interface RefreshResult {
  ok: boolean;
  reason?: RefreshFailureReason;
}

// Module-level mutex — all callers (useValidateAuth, useProactiveTokenRefresh,
// DynamicBaseQuery 401 handler, socket recovery) share this so only ONE refresh
// request flies at a time across the whole app.
let _pendingSilentRefresh: Promise<RefreshResult> | null = null;

// Hard ceiling — a hung network must NOT leave the boot spinner stuck with the
// mutex locked forever.
const SILENT_REFRESH_TIMEOUT_MS = 15_000;

export const silentRefresh = (): Promise<RefreshResult> => {
  if (_pendingSilentRefresh) return _pendingSilentRefresh;

  _pendingSilentRefresh = (async (): Promise<RefreshResult> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SILENT_REFRESH_TIMEOUT_MS);
    try {
      const stored = getAuthTokensFromLocalStorage();
      if (!stored?.[ENUM_AUTH_TOKENS.REFRESH_TOKEN])
        return { ok: false, reason: "no-token" };

      const response = await fetch(`${ENV_VARS.API_INSTANCE}${AUTH_REFRESH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          refreshToken: stored[ENUM_AUTH_TOKENS.REFRESH_TOKEN],
        }),
        signal: controller.signal,
      });

      // 5xx = the server is broken/booting, not a verdict on the token.
      if (!response.ok)
        return {
          ok: false,
          reason: response.status >= 500 ? "network" : "rejected",
        };

      const json = (await response.json()) as RefreshApiResponse;
      const accessToken = json?.data?.accessToken;
      const refreshToken = json?.data?.refreshToken;

      // Never store empty tokens (would loop 401 → refresh → 401 until logout).
      if (!accessToken || !refreshToken)
        return { ok: false, reason: "rejected" };

      setAuthTokensToCookie(
        accessToken,
        refreshToken,
        stored[ENUM_AUTH_TOKENS.DEVICE_ID],
        stored[ENUM_AUTH_TOKENS.REMEMBERME],
      );

      return { ok: true };
    } catch {
      // Network error or timeout/abort → the BE never gave a verdict.
      return { ok: false, reason: "network" };
    } finally {
      clearTimeout(timeout);
      _pendingSilentRefresh = null;
    }
  })();

  return _pendingSilentRefresh;
};

// Reachability probe for the connection-lost screen. Hits the public /health
// route — no auth, no DB — so it answers the moment the API is listening again.
const HEALTH_PROBE_TIMEOUT_MS = 4_000;

export const probeServerReachable = async (): Promise<boolean> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(`${ENV_VARS.API_INSTANCE}${API_HEALTH}`, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};
